// Grants: permission for a principal, or a whole other tenant, on packages in a namespace (docs/api.md 2.4).
import { useState, type FormEvent } from "react";
import type { Action, Ecosystem, Grant, GrantInput, TenantDetail } from "../api/types";
import { Badge } from "../components/Badge";
import { useServices } from "../components/context";
import { IdCell } from "../components/IdCell";
import { PendingFilter, useGrace, type DeleteTarget, type PendingRow } from "../components/Grace";
import { ErrorBanner, Section, useAction } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { formatTime, PACKAGE_GLOB_RE, SLUG_RE } from "../lib/format";
import { useDomains } from "../components/useDomains";
import { initialNamespace, lastNamespace, rememberNamespace } from "../lib/namespaces";
import { noTenantGrants, PAUSED_TEXT, planName, tenantGrantHint } from "../lib/plans";
import {
  actionsFor,
  ECOSYSTEM_HELP,
  ECOSYSTEM_LABEL,
  ECOSYSTEMS,
  GLOB_PLACEHOLDER,
  grantEcosystem,
  keepAllowed,
} from "../lib/grants";
import { registryName } from "../lib/snippets";
import { atLeast } from "../lib/roles";

/** Why `yank` is greyed out for a Go grant (ADR-0038 section 3). */
export const GO_YANK_TEXT = 'Go does not use yank. Publish a newer version with a "retract" line in go.mod.';

/** Builds a grant body from the form. Returns an error message or the body. */
export function buildGrant(f: {
  namespace: string;
  ecosystem: Ecosystem;
  glob: string;
  actions: Action[];
  granteeKind: "principal" | "tenant";
  principalId: string;
  tenantSlug: string;
  expiresOn: string;
  plan: TenantDetail["plan"];
}): { error: string } | { body: GrantInput } {
  if (!f.namespace) return { error: "Choose a namespace." };
  const glob = f.glob.trim();
  if (!glob) return { error: "Enter a package glob." };
  // The OCI and `any` glob rules are checked by the registry, and the form shows its 400 message as it is.
  if (f.ecosystem === "cargo" && !PACKAGE_GLOB_RE.test(glob))
    return { error: "Use a crate name, a name ending with *, or * alone." };
  if (f.actions.length === 0) return { error: "Choose at least one permission." };
  const wrong = f.actions.find((a) => !actionsFor(f.ecosystem).includes(a));
  if (wrong)
    return { error: `"${wrong}" is not a permission for ${ECOSYSTEM_LABEL[f.ecosystem].toLowerCase()}.` };
  let grantee: GrantInput["grantee"];
  if (f.granteeKind === "principal") {
    if (!f.principalId) return { error: "Choose a principal." };
    grantee = { principal_id: f.principalId };
  } else {
    if (f.plan === "indie" || f.plan === "consumer")
      return { error: `The ${planName(f.plan)} plan cannot share packages with other tenants.` };
    if (!SLUG_RE.test(f.tenantSlug)) return { error: "Enter the slug of the other tenant." };
    if (f.actions.some((a) => a !== "read")) return { error: "A grant to another tenant allows read only." };
    grantee = { tenant_slug: f.tenantSlug };
  }
  let expires_at: string | null = null;
  if (f.expiresOn) {
    const d = new Date(`${f.expiresOn}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return { error: "The expiry date is not valid." };
    if (d.getTime() <= Date.now()) return { error: "The expiry date must be in the future." };
    expires_at = d.toISOString().replace(/\.\d+Z$/, "Z");
  }
  return {
    body: {
      namespace: f.namespace,
      ecosystem: f.ecosystem,
      package_glob: glob,
      actions: f.actions,
      grantee,
      expires_at,
    },
  };
}

function granteeLabel(g: Grant, names: Map<string, string>): string {
  if ("tenant_slug" in g.grantee) return `tenant ${g.grantee.tenant_slug}`;
  return names.get(g.grantee.principal_id) ?? g.grantee.principal_id;
}

/**
 * The delete dialog for a grant. A grant to another tenant is "in use" (ADR-0031 section 5), so the admin
 * types the grantee tenant slug. Other grants need one confirm click.
 */
export function grantDeleteTarget(g: Grant, names: Map<string, string>): DeleteTarget {
  const cross = "tenant_slug" in g.grantee ? g.grantee.tenant_slug : undefined;
  return {
    id: g.id,
    title: `the grant on ${g.namespace}/${g.package_glob}`,
    message: cross
      ? `Tenant ${cross} loses ${g.actions.join(", ")} on ${g.namespace}/${g.package_glob}. Its pulls fail within about 60 seconds.`
      : `${granteeLabel(g, names)} loses ${g.actions.join(", ")} on ${g.namespace}/${g.package_glob} within about 60 seconds.`,
    ...(cross ? { typedName: cross, typedLabel: "grantee tenant slug" } : {}),
    impact: "grants",
    inUseName: `${g.namespace}/${g.package_glob}`,
    inUseLabel: "namespace and packages",
  };
}

/** `ns` is the namespace in context (a link from the Namespaces page). */
export function Grants({ tenant, ns }: { tenant: TenantDetail; ns?: string }) {
  const { api } = useServices();
  const list = useLoad(() => api.listGrants(tenant.slug), [tenant.slug]);
  const nss = useLoad(() => api.listNamespaces(tenant.slug), [tenant.slug]);
  const prns = useLoad(() => api.listPrincipals(tenant.slug), [tenant.slug]);
  const action = useAction();
  const isAdmin = atLeast(tenant.role, "admin");
  const domains = useDomains(tenant);
  const tenantGrantsOff = noTenantGrants(tenant);
  const slugHint = tenantGrantHint({ ...tenant, domains: domains.data ?? tenant.domains });

  const [namespace, setNamespace] = useState("");
  const [last] = useState(() => lastNamespace(tenant.slug));
  // "" shows the grants of all namespaces.
  const [filter, setFilter] = useState(ns ?? "");
  const [ecosystem, setEcosystem] = useState<Ecosystem>("cargo");
  const [glob, setGlob] = useState("*");
  const [actions, setActions] = useState<Action[]>(["read"]);
  const [granteeKind, setGranteeKind] = useState<"principal" | "tenant">("principal");
  const [principalId, setPrincipalId] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const nsList = nss.data?.items ?? [];
  const prnList = prns.data?.items ?? [];
  const names = new Map(prnList.map((p) => [p.id, p.name]));
  const formNamespace =
    namespace ||
    initialNamespace(
      nsList.map((n) => n.slug),
      ns,
      last,
    );
  const shown = (g: Grant) => !filter || g.namespace === filter;
  const grants = (list.data?.items ?? []).filter(shown);
  const grace = useGrace<Grant>({ tenant, collection: "grants", onChange: list.reload });
  const pendingRows: PendingRow[] = grace.items.filter(shown).map((g) => ({
    id: g.id,
    title: (
      <span className="mono">
        {g.namespace}/{g.package_glob}
      </span>
    ),
    detail: `${ECOSYSTEM_LABEL[grantEcosystem(g)]}: ${g.actions.join(", ")} for ${granteeLabel(g, names)}`,
    deletedBy: g.deleted_by,
    deletedAt: g.deleted_at,
    purgeAfter: g.purge_after,
    purge: {
      id: g.id,
      title: `the grant on ${g.namespace}/${g.package_glob}`,
      confirm: g.id,
      confirmLabel: "grant ID",
    },
  }));

  const formActions = actionsFor(ecosystem);

  function toggle(a: Action) {
    setActions((cur) =>
      cur.includes(a) ? cur.filter((x) => x !== a) : formActions.filter((x) => x === a || cur.includes(x)),
    );
  }

  function chooseEcosystem(e: Ecosystem) {
    setEcosystem(e);
    setActions((cur) => keepAllowed(cur, e));
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const r = buildGrant({
      namespace: formNamespace,
      ecosystem,
      glob,
      actions,
      granteeKind,
      principalId: principalId || prnList[0]?.id || "",
      tenantSlug,
      expiresOn,
      plan: tenant.plan,
    });
    if ("error" in r) {
      setFormError(r.error);
      return;
    }
    setFormError(null);
    rememberNamespace(tenant.slug, r.body.namespace);
    if (await action.run(async () => void (await api.createGrant(tenant.slug, r.body)))) {
      setGlob("*");
      setExpiresOn("");
      list.reload();
    }
  }

  return (
    <>
      <Section
        title="Grants"
        actions={
          <>
            <label className="inline">
              Namespace{" "}
              <select
                aria-label="Show grants in namespace"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="">All namespaces</option>
                {nsList.map((n) => (
                  <option key={n.id} value={n.slug}>
                    {n.slug}
                  </option>
                ))}
                {filter && !nsList.some((n) => n.slug === filter) && <option value={filter}>{filter}</option>}
              </select>
            </label>
            <PendingFilter view={grace.view} setView={grace.setView} count={grace.count} />
          </>
        }
      >
        <p className="muted">
          A grant gives access to packages of one type in one namespace. "publish", "yank" and "delete"
          include "read". "yank" is for crates. "delete" is for containers and Go modules. A grant to another
          tenant allows read only.
        </p>
        <ErrorBanner error={list.error ?? action.error ?? grace.error} />
        {grace.view === "pending" ? (
          grace.rowsTable(pendingRows)
        ) : (
          <table className="stack data-table">
            <thead>
              <tr>
                <th>Namespace</th>
                <th>Type</th>
                <th>Packages</th>
                <th>Permissions</th>
                <th>Grantee</th>
                <th>Expires</th>
                <th>ID</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => (
                <tr key={g.id}>
                  <td className="mono" data-label="Namespace">
                    {g.namespace}
                  </td>
                  <td data-label="Type">{ECOSYSTEM_LABEL[grantEcosystem(g)]}</td>
                  <td className="mono" data-label="Packages">
                    {g.package_glob}
                  </td>
                  <td data-label="Permissions">{g.actions.join(", ")}</td>
                  <td data-label="Grantee">
                    {g.paused_reason && (
                      <>
                        <span className="tag expired" title={PAUSED_TEXT[g.paused_reason]}>
                          paused: {g.paused_reason}
                        </span>{" "}
                      </>
                    )}
                    {"tenant_slug" in g.grantee ? (
                      <>
                        <Badge kind="tenant" /> {g.grantee.tenant_slug}
                      </>
                    ) : (
                      <>
                        <Badge kind="principal" /> {granteeLabel(g, names)}
                      </>
                    )}
                  </td>
                  <td data-label="Expires">{formatTime(g.expires_at)}</td>
                  <IdCell id={g.id} />
                  <td className="actions" data-label="Actions">
                    {isAdmin && (
                      <button
                        type="button"
                        className="danger"
                        disabled={action.busy || grace.busy}
                        onClick={() => grace.askDelete(grantDeleteTarget(g, names))}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {grace.view !== "pending" && list.data && grants.length === 0 && (
          <p className="muted">
            {filter ? `No grants in namespace ${filter}.` : "No grants yet. Add one below."}
          </p>
        )}
      </Section>
      {grace.ui}
      {isAdmin && (
        <Section title="Add a grant">
          <form onSubmit={onCreate} className="form">
            <label className="field-primary">
              Namespace
              <select
                value={formNamespace}
                onChange={(e) => {
                  setNamespace(e.target.value);
                  rememberNamespace(tenant.slug, e.target.value);
                }}
              >
                {nsList.map((n) => (
                  <option key={n.id} value={n.slug}>
                    {n.slug}
                  </option>
                ))}
              </select>
              {formNamespace && (
                <small className="muted">
                  The grant applies only to packages in this namespace (Cargo registry{" "}
                  <code>{registryName(tenant.slug, formNamespace)}</code>).
                </small>
              )}
            </label>
            <label>
              Package type
              <select value={ecosystem} onChange={(e) => chooseEcosystem(e.target.value as Ecosystem)}>
                {ECOSYSTEMS.map((e) => (
                  <option key={e} value={e}>
                    {ECOSYSTEM_LABEL[e]} ({e})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Packages (package glob)
              <input
                required
                value={glob}
                onChange={(e) => setGlob(e.target.value)}
                placeholder={GLOB_PLACEHOLDER[ecosystem]}
              />
              <small className="muted">{ECOSYSTEM_HELP[ecosystem]}</small>
            </label>
            <fieldset className="row">
              <legend>Permissions</legend>
              {formActions.map((a) => (
                <label key={a} className="check">
                  <input type="checkbox" checked={actions.includes(a)} onChange={() => toggle(a)} /> {a}
                </label>
              ))}
              {ecosystem === "go" && (
                <label className="check muted" title={GO_YANK_TEXT}>
                  <input type="checkbox" checked={false} disabled aria-describedby="go-yank" /> yank
                  <small id="go-yank"> {GO_YANK_TEXT}</small>
                </label>
              )}
            </fieldset>
            <fieldset className="row">
              <legend>Grantee</legend>
              <label className="check">
                <input
                  type="radio"
                  name="gk"
                  checked={granteeKind === "principal"}
                  onChange={() => setGranteeKind("principal")}
                />{" "}
                A principal in this tenant
              </label>
              {tenantGrantsOff ? (
                <small className="muted">
                  The {planName(tenant.plan)} plan cannot grant to other tenants. That needs the Organization
                  or Publisher plan.
                </small>
              ) : (
                <label className="check">
                  <input
                    type="radio"
                    name="gk"
                    checked={granteeKind === "tenant"}
                    onChange={() => {
                      setGranteeKind("tenant");
                      setActions(["read"]);
                    }}
                  />{" "}
                  All principals of another tenant
                </label>
              )}
            </fieldset>
            {granteeKind === "principal" ? (
              <label>
                Principal
                <select
                  value={principalId || prnList[0]?.id || ""}
                  onChange={(e) => setPrincipalId(e.target.value)}
                >
                  {prnList.length === 0 && <option value="">No principals</option>}
                  {prnList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.kind})
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label>
                Tenant slug
                <input
                  required
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value.toLowerCase())}
                  placeholder="customer-b"
                />
                {slugHint && <small className="muted">{slugHint}</small>}
              </label>
            )}
            <label>
              Expires on (optional, UTC)
              <input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
            </label>
            {formError && (
              <p className="banner error" role="alert">
                {formError}
              </p>
            )}
            <button type="submit" disabled={action.busy}>
              Add grant
            </button>
          </form>
        </Section>
      )}
    </>
  );
}
